import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@material-ui/core/CircularProgress';
import ProjectionsCard from './Projections/ProjectionsCard';
import alt_rcc_lang from '../../../../resources/missing_rcc_.png';
import alt_lcc_lang from '../../../../resources/missing_lcc_.png';
import alt_rmlo_lang from '../../../../resources/missing_rmlo_.png';
import alt_lmlo_lang from '../../../../resources/missing_lmlo_.png';
import alt_rcc from '../../../../resources/missing_rcc.png';
import alt_lcc from '../../../../resources/missing_lcc.png';
import alt_rmlo from '../../../../resources/missing_rmlo.png';
import alt_lmlo from '../../../../resources/missing_lmlo.png';
import CapturesDialog from './CapturesDialog/CapturesDialog';
import { setLoadedProjections } from '../../../../store';
import ZoomView from './ZoomView/ZoomView';
import animation from '../../../../resources/processing_animation.gif';
import { Tooltip, Typography } from '@material-ui/core';

// Image mapping object
const IMAGE_MAPPING = {
  rcc: {
    default: alt_rcc,
    lang: alt_rcc_lang,
  },
  lcc: {
    default: alt_lcc,
    lang: alt_lcc_lang,
  },
  rmlo: {
    default: alt_rmlo,
    lang: alt_rmlo_lang,
  },
  lmlo: {
    default: alt_lmlo,
    lang: alt_lmlo_lang,
  },
};

const useStyles = makeStyles((theme) => ({
  projections_view_container: {
    width: '100%',
    height: '100%',
    borderRadius: '0px 0px 5px 5px',
    backgroundColor: theme.palette.background.mainDark,
    display: 'flex',
    justifyContent: 'center',
    justifyItems: 'center',
    alignItems: 'center',
    padding: '0',
  },

  onLoad: {
    opacity: '1!important',
    transition: 'opacity 0.4s',
    display: 'flex',
    alignItems: 'center',
  },

  hidden: {
    opacity: 0,
  },

  spinner_loader: {
    position: 'absolute',
    zIndex: 1000,
    opacity: 0.7,
  },

  proj_missing_img: {
    borderRadius: '5px',
    margin: 'auto',
    maxWidth: '90%',
    height: '90%',
    zIndex: 1,
  },

  proj_missing_img_v: {
    maxWidth: '90%',
    height: '90%',
    borderRadius: '5px',
    margin: 'auto',
  },

  spinner_loader_text: {
    fontSize: '12px',
    color: '#b3b3b3',
    marginTop: '10px',
    position: 'absolute',
    display: 'flex',
    top: '5%',
    fontStyle: 'italic',
    letterSpacing: '0.1rem',
    backgroundColor: theme.palette.background.mainDark,
    zIndex: 100,
    padding: '10px',
    borderRadius: '5px',
  },

  spinner_loader_animation: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    position: 'absolute',
    opacity: 0.6,
    zIndex: 100,
  },

  spinner_loader_tooltip: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    fontSize: '12px',
  },
}));

const ProjectionsView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();

  const [secondCaptureOpen, setSecondCaptureOpen] = useState(null);
  const [projFlipped, setProjFlipped] = useState([false, false, false, false]);
  const [isLoading, setIsLoading] = useState(false);
  const [invertedColor, setInvertedColor] = useState({ rcc: false, rmlo: false, lcc: false, lmlo: false });

  // Store state
  const results = useSelector((state) => state.results);
  const images = useSelector((state) => state.images);
  const currentExam = useSelector((state) => state.examinations.currentExam);
  const currentlyProcessed = useSelector((state) => state.examinations.currentlyProcessed);
  const { landscape, tabsIndex } = useSelector((state) => state.gui);
  const globalSettings = useSelector((store) => store.global_settings ?? {});
  const { language } = globalSettings;
  const { localChangesQuality, localChangesOpacities, localChangesDensity, localChangesMicrocalc } =
    useSelector((state) => state.labeling);

  const { folder, projections, second_captures, error } = results;

  const hasLocalChanges =
    localChangesQuality || localChangesOpacities || localChangesDensity || localChangesMicrocalc;

  // Reset invertedColor when tabsIndex changes
  useEffect(() => {
    setInvertedColor({ rcc: false, rmlo: false, lcc: false, lmlo: false });
  }, [tabsIndex, results]);

  // Memoized values
  const expectedProjections = useMemo(() => Object.values(projections).filter((l) => !!l), [projections]);

  // Effects
  useEffect(() => {
    setProjFlipped([false, false, false, false]);
    setSecondCaptureOpen(null);
  }, [folder]);

  useEffect(() => {
    const loading =
      !!currentExam &&
      (images.loadedProjections === 0 || images.loadedProjections < expectedProjections.length);
    setIsLoading(loading);
    if (error) {
      setIsLoading(false);
    }
  }, [images.loadedProjections, expectedProjections.length, currentExam, error]);

  const handleProjectionLoad = useCallback(() => {
    dispatch(setLoadedProjections(images.loadedProjections + 1));
  }, [dispatch, images.loadedProjections]);

  // Memoized projection cards
  const renderProjectionCard = useCallback(
    (projName, left = false) => {
      const hasProjection = !!projections[projName];
      // const hasSecondCaptures = second_captures?.[projName]?.length > 0;
      const altImage =
        language === 'en' || !language ? IMAGE_MAPPING[projName].default : IMAGE_MAPPING[projName].lang;
      if (hasProjection) {
        return (
          <ProjectionsCard
            key={`${projName}-${JSON.stringify(invertedColor)}`}
            left={left}
            proj_name={projName}
            onLoad={handleProjectionLoad}
            uid={results?.projections?.[projName]?.uid}
            invertedColor={invertedColor}
            setInvertedColor={setInvertedColor}
          />
        );
      }

      return (
        <Tooltip arrow title={projName.toUpperCase() + ': ' + t('ResultView.missing')}>
          <span>
            <img
              key={projName}
              className={landscape ? classes.proj_missing_img : classes.proj_missing_img_v}
              src={altImage}
              alt={`${projName.toUpperCase()} missing`}
              style={{
                maxHeight: landscape ? '43vh' : 'calc(25vh - 100px)',
                background: theme.palette.background.mainDark,
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </span>
        </Tooltip>
      );
    },
    [
      projections,
      second_captures,
      landscape,
      tabsIndex,
      hasLocalChanges,
      handleProjectionLoad,
      results,
      invertedColor,
    ],
  );

  // Get zoom view classes
  const zoomViewClasses = useMemo(() => {
    const classList = [classes.hidden];
    if (!isLoading) {
      classList.push(classes.onLoad);
    }
    return classList.join(' ');
  }, [isLoading, classes]);

  return (
    <div className={classes.projections_view_container}>
      {(isLoading || (currentlyProcessed && currentlyProcessed === currentExam)) &&
        (currentlyProcessed === currentExam ? (
          <>
            <Typography className={classes.spinner_loader_text}>
              {t('ResultView.evaluating_current_examination')}
            </Typography>
            <Tooltip
              arrow
              interactive
              placement='top'
              title={
                <Typography className={classes.spinner_loader_tooltip}>
                  {t('ResultView.evaluating_current_examination')}
                </Typography>
              }>
              <img src={animation} alt='logoanimation' className={classes.spinner_loader_animation} />
            </Tooltip>
          </>
        ) : (
          <CircularProgress size={50} className={classes.spinner_loader} />
        ))}

      <ZoomView
        className={zoomViewClasses}
        projectionRcc={projections?.rcc}
        projectionLcc={projections?.lcc}
        projectionLmlo={projections?.lmlo}
        projectionRmlo={projections?.rmlo}
        invertedColor={invertedColor}
        setInvertedColor={setInvertedColor}
        setSecondCaptureOpen={setSecondCaptureOpen}
        secondCaptureOpen={secondCaptureOpen}>
        {renderProjectionCard('rcc')}
        {renderProjectionCard('lcc', true)}
        {renderProjectionCard('rmlo')}
        {renderProjectionCard('lmlo', true)}
      </ZoomView>

      {!!secondCaptureOpen && (
        <CapturesDialog
          openDialog={!!secondCaptureOpen}
          onClose={() => setSecondCaptureOpen(null)}
          proj_name={secondCaptureOpen}
          uids={[
            ...second_captures[secondCaptureOpen],
            projections[secondCaptureOpen] ? projections[secondCaptureOpen].uid : '',
          ]}
        />
      )}
    </div>
  );
};

export default React.memo(ProjectionsView);
